/**
 * User UI Service
 * 使用者自定義 UI 元件管理服務
 */

import { supabase } from '../core/supabaseClient';

// =============================================
// Database Types (snake_case - matches DB schema)
// =============================================

interface UserUIRow {
    id: string;
    short_id: string;
    organization_id: string;
    field_id: string | null;
    name: string;
    description: string | null;
    current_version: number;
    is_active: boolean;
    is_pinned: boolean;
    pin_order: number;
    created_by: string;
    created_at: string;
    updated_by: string;
    updated_at: string;
    deleted_at: string | null;
    versions?: UserUIVersionRow[];
}

interface UserUIVersionRow {
    id: string;
    ui_id: string;
    version: number;
    prompt: string;
    source_code: string | null;
    build_status: BuildStatus;
    build_error: string | null;
    build_metadata: Record<string, unknown> | null;
    created_by: string;
    created_at: string;
}

// =============================================
// API Types (camelCase - used in application)
// =============================================

/** 建置狀態 */
export type BuildStatus = 'pending' | 'building' | 'success' | 'failed';

/** UI 資料 */
export interface UserUI {
    id: string;
    shortId: string;
    organizationId: string;
    fieldId: string | null;
    name: string;
    description: string | null;
    currentVersion: number;
    isActive: boolean;
    isPinned: boolean;
    pinOrder: number;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    versions: UserUIVersion[];
}

/** UI 版本資料 */
export interface UserUIVersion {
    id: string;
    uiId: string;
    version: number;
    prompt: string;
    sourceCode: string | null;
    buildStatus: BuildStatus;
    buildError: string | null;
    buildMetadata: Record<string, unknown> | null;
    createdBy: string;
    createdAt: string;
}

/** 建立 UI 參數 */
export interface CreateUIParams {
    organizationId: string;
    fieldId?: string | null;
    name?: string;
    description?: string | null;
    createdBy: string;
}

/** 更新 UI 參數 */
export interface UpdateUIParams {
    name?: string;
    description?: string | null;
    currentVersion?: number;
    isActive?: boolean;
    fieldId?: string | null;
}

/** 建立版本參數 */
export interface CreateVersionParams {
    prompt: string;
    sourceCode?: string | null;
    buildStatus?: BuildStatus;
    buildMetadata?: Record<string, unknown> | null;
    createdBy: string;
}

// =============================================
// Service Implementation
// =============================================

export const userUIService = {
    // ==================== UI CRUD ====================

    /**
     * 建立新 UI
     * @param data - 建立參數
     * @returns 建立的 UI 資料
     */
    async createUI(data: CreateUIParams): Promise<UserUI> {
        const { data: result, error } = await supabase
            .from('user_ui')
            .insert({
                organization_id: data.organizationId,
                field_id: data.fieldId || null,
                name: data.name || '未命名 UI',
                description: data.description || null,
                created_by: data.createdBy,
                updated_by: data.createdBy
            })
            .select()
            .single();

        if (error) throw error;
        return this.transformUIData(result as UserUIRow)!;
    },

    /**
     * 取得組織所有 UI
     * @param organizationId - 組織 ID
     * @returns UI 列表
     */
    async getUIsByOrganization(organizationId: string): Promise<UserUI[]> {
        const { data, error } = await supabase
            .from('user_ui')
            .select(`
                *,
                versions:user_ui_versions(id, version, build_status, created_at)
            `)
            .eq('organization_id', organizationId)
            .is('deleted_at', null)
            .eq('is_active', true)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return ((data as UserUIRow[]) || []).map(ui => this.transformUIData(ui)!);
    },

    /**
     * 取得單一 UI（含所有版本）
     * @param uiId - UI ID
     * @returns UI 資料
     */
    async getUI(uiId: string): Promise<UserUI> {
        const { data, error } = await supabase
            .from('user_ui')
            .select(`
                *,
                versions:user_ui_versions(*)
            `)
            .eq('id', uiId)
            .is('deleted_at', null)
            .single();

        if (error) throw error;
        return this.transformUIData(data as UserUIRow)!;
    },

    /**
     * 透過 short_id 取得 UI
     * @param shortId - 簡短 ID（如 a1b2c3d4）
     * @returns UI 資料
     */
    async getUIByShortId(shortId: string): Promise<UserUI> {
        const { data, error } = await supabase
            .from('user_ui')
            .select(`
                *,
                versions:user_ui_versions(*)
            `)
            .eq('short_id', shortId)
            .is('deleted_at', null)
            .single();

        if (error) throw error;
        return this.transformUIData(data as UserUIRow)!;
    },

    /**
     * 更新 UI 基本資訊
     * @param uiId - UI ID
     * @param updates - 要更新的欄位
     * @param userId - 更新者 ID
     * @returns 更新後的 UI 資料
     */
    async updateUI(uiId: string, updates: UpdateUIParams, userId: string): Promise<UserUI> {
        const updateData: Record<string, unknown> = { updated_by: userId };

        if (updates.name !== undefined) updateData.name = updates.name;
        if (updates.description !== undefined) updateData.description = updates.description;
        if (updates.currentVersion !== undefined) updateData.current_version = updates.currentVersion;
        if (updates.isActive !== undefined) updateData.is_active = updates.isActive;
        if (updates.fieldId !== undefined) updateData.field_id = updates.fieldId;

        const { data, error } = await supabase
            .from('user_ui')
            .update(updateData)
            .eq('id', uiId)
            .select()
            .single();

        if (error) throw error;
        return this.transformUIData(data as UserUIRow)!;
    },

    /**
     * 軟刪除 UI
     * @param uiId - UI ID
     */
    async deleteUI(uiId: string): Promise<void> {
        const { error } = await supabase
            .from('user_ui')
            .update({
                deleted_at: new Date().toISOString(),
                is_active: false
            })
            .eq('id', uiId);

        if (error) throw error;
    },

    // ==================== 版本管理 ====================

    /**
     * 建立新版本
     * @param uiId - UI ID
     * @param data - 版本資料
     * @returns 建立的版本資料
     */
    async createVersion(uiId: string, data: CreateVersionParams): Promise<UserUIVersion> {
        // 取得下一個版本號
        const { data: existing } = await supabase
            .from('user_ui_versions')
            .select('version')
            .eq('ui_id', uiId)
            .order('version', { ascending: false })
            .limit(1);

        const nextVersion = existing?.length && existing.length > 0 ? (existing[0] as { version: number }).version + 1 : 1;

        const { data: result, error } = await supabase
            .from('user_ui_versions')
            .insert({
                ui_id: uiId,
                version: nextVersion,
                prompt: data.prompt,
                source_code: data.sourceCode || null,
                build_status: data.buildStatus || 'pending',
                build_metadata: data.buildMetadata || null,
                created_by: data.createdBy
            })
            .select()
            .single();

        if (error) throw error;

        // 更新父表的當前版本
        await this.updateUI(uiId, { currentVersion: nextVersion }, data.createdBy);

        return this.transformVersionData(result as UserUIVersionRow)!;
    },

    /**
     * 取得版本歷史
     * @param uiId - UI ID
     * @returns 版本列表（從新到舊）
     */
    async getVersionHistory(uiId: string): Promise<UserUIVersion[]> {
        const { data, error } = await supabase
            .from('user_ui_versions')
            .select('*')
            .eq('ui_id', uiId)
            .order('version', { ascending: false });

        if (error) throw error;
        return ((data as UserUIVersionRow[]) || []).map(v => this.transformVersionData(v)!);
    },

    /**
     * 取得特定版本
     * @param uiId - UI ID
     * @param version - 版本號
     * @returns 版本資料
     */
    async getVersion(uiId: string, version: number): Promise<UserUIVersion> {
        const { data, error } = await supabase
            .from('user_ui_versions')
            .select('*')
            .eq('ui_id', uiId)
            .eq('version', version)
            .single();

        if (error) throw error;
        return this.transformVersionData(data as UserUIVersionRow)!;
    },

    /**
     * 取得當前版本
     * @param uiId - UI ID
     * @returns 當前版本資料，如果沒有版本則返回 null
     */
    async getCurrentVersion(uiId: string): Promise<UserUIVersion | null> {
        const ui = await this.getUI(uiId);
        if (!ui || ui.currentVersion === 0) return null;
        return this.getVersion(uiId, ui.currentVersion);
    },

    /**
     * 切換到指定版本（設為當前版本）
     * @param uiId - UI ID
     * @param version - 版本號
     * @param userId - 操作者 ID
     * @returns 更新後的 UI 資料
     */
    async switchToVersion(uiId: string, version: number, userId: string): Promise<UserUI> {
        return this.updateUI(uiId, { currentVersion: version }, userId);
    },

    /**
     * 更新版本建置狀態
     * @param versionId - 版本 ID
     * @param status - 狀態
     * @param error - 錯誤訊息（僅失敗時）
     * @param metadata - 建置元資料
     */
    async updateVersionStatus(
        versionId: string,
        status: BuildStatus,
        error: string | null = null,
        metadata: Record<string, unknown> | null = null
    ): Promise<void> {
        const updateData: Record<string, unknown> = { build_status: status };
        if (error) updateData.build_error = error;
        if (metadata) updateData.build_metadata = metadata;

        const { error: err } = await supabase
            .from('user_ui_versions')
            .update(updateData)
            .eq('id', versionId);

        if (err) throw err;
    },

    /**
     * 更新版本原始碼
     * @param versionId - 版本 ID
     * @param sourceCode - 原始碼
     */
    async updateVersionSourceCode(versionId: string, sourceCode: string): Promise<void> {
        const { error } = await supabase
            .from('user_ui_versions')
            .update({
                source_code: sourceCode,
                build_status: 'success'
            })
            .eq('id', versionId);

        if (error) throw error;
    },

    // ==================== 輔助方法 ====================

    /**
     * 轉換 UI 資料格式
     * @param data - 資料庫格式資料
     * @returns API 格式資料
     */
    transformUIData(data: UserUIRow | null): UserUI | null {
        if (!data) return null;
        return {
            id: data.id,
            shortId: data.short_id,
            organizationId: data.organization_id,
            fieldId: data.field_id,
            name: data.name,
            description: data.description,
            currentVersion: data.current_version,
            isActive: data.is_active,
            isPinned: data.is_pinned || false,
            pinOrder: data.pin_order || 0,
            createdBy: data.created_by,
            createdAt: data.created_at,
            updatedBy: data.updated_by,
            updatedAt: data.updated_at,
            // 關聯資料
            versions: data.versions
                ? data.versions
                    .map(v => this.transformVersionData(v)!)
                    .sort((a, b) => b.version - a.version)
                : []
        };
    },

    /**
     * 轉換版本資料格式
     * @param data - 資料庫格式資料
     * @returns API 格式資料
     */
    transformVersionData(data: UserUIVersionRow | null): UserUIVersion | null {
        if (!data) return null;
        return {
            id: data.id,
            uiId: data.ui_id,
            version: data.version,
            prompt: data.prompt,
            sourceCode: data.source_code,
            buildStatus: data.build_status,
            buildError: data.build_error,
            buildMetadata: data.build_metadata,
            createdBy: data.created_by,
            createdAt: data.created_at
        };
    },

    /**
     * 格式化顯示 ID
     * @param shortId - 簡短 ID
     * @returns 格式化的 ID（如 UI-a1b2c3d4）
     */
    formatDisplayId(shortId: string): string {
        return `UI-${shortId}`;
    },

    // ==================== 釘選功能 ====================

    /**
     * 取得已釘選的工具（按 pin_order 排序）
     * @param organizationId - 組織 ID
     * @returns 已釘選的 UI 列表
     */
    async getPinnedTools(organizationId: string): Promise<UserUI[]> {
        const { data, error } = await supabase
            .from('user_ui')
            .select(`
                *,
                versions:user_ui_versions(id, version, build_status, created_at)
            `)
            .eq('organization_id', organizationId)
            .eq('is_pinned', true)
            .is('deleted_at', null)
            .eq('is_active', true)
            .order('pin_order', { ascending: true });

        if (error) throw error;
        return ((data as UserUIRow[]) || []).map(ui => this.transformUIData(ui)!);
    },

    /**
     * 釘選工具
     * @param uiId - UI ID
     * @param userId - 操作者 ID
     */
    async pinTool(uiId: string, userId: string): Promise<void> {
        // 先取得該 UI 的組織 ID
        const ui = await this.getUI(uiId);
        if (!ui) throw new Error('找不到此 UI');

        // 取得當前最大的 pin_order
        const { data: existingPinned } = await supabase
            .from('user_ui')
            .select('pin_order')
            .eq('organization_id', ui.organizationId)
            .eq('is_pinned', true)
            .is('deleted_at', null)
            .order('pin_order', { ascending: false })
            .limit(1);

        const nextOrder = existingPinned?.length && existingPinned.length > 0
            ? (existingPinned[0] as { pin_order: number }).pin_order + 1
            : 1;

        // 更新為已釘選
        const { error } = await supabase
            .from('user_ui')
            .update({
                is_pinned: true,
                pin_order: nextOrder,
                updated_by: userId
            })
            .eq('id', uiId);

        if (error) throw error;
    },

    /**
     * 取消釘選工具
     * @param uiId - UI ID
     * @param userId - 操作者 ID
     */
    async unpinTool(uiId: string, userId: string): Promise<void> {
        const { error } = await supabase
            .from('user_ui')
            .update({
                is_pinned: false,
                pin_order: 0,
                updated_by: userId
            })
            .eq('id', uiId);

        if (error) throw error;
    },

    /**
     * 更新釘選順序
     * @param uiId - UI ID
     * @param order - 新的順序
     * @param userId - 操作者 ID
     */
    async updatePinOrder(uiId: string, order: number, userId: string): Promise<void> {
        const { error } = await supabase
            .from('user_ui')
            .update({
                pin_order: order,
                updated_by: userId
            })
            .eq('id', uiId);

        if (error) throw error;
    }
};

export default userUIService;
